/**
 * MissionControlOrderRow — the inline-expandable cockpit row.
 *
 * Replaces the modal-driven OrderManagementModal flow on the new
 * Action Queue. Each row is a self-contained "mini mission-control
 * card":
 *
 *   Left  — proof thumbnail, order #, customer, age timer
 *   Mid   — stage chip, supplier chip, proof status, revision count
 *   Right — action buttons (Review · Send to Supplier · Update Status)
 *
 * Expanded panel hosts the SupplierPicker + Send button + dispatch
 * history, all inline so admins never lose their place in the queue.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Truck,
  FileText, Clock, Loader2, Mail, ExternalLink,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ProofThumbnail } from '../../../components/ProofThumbnail';
import { SupplierPicker } from './SupplierPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_LABEL = {
  paid: 'New Paid',
  file_review: 'File Review',
  proof_sent: 'Proof Sent',
  awaiting_approval: 'Awaiting Approval',
  production: 'Production',
  shipped: 'Shipped',
  completed: 'Completed',
};
const STAGE_BG = {
  paid: 'bg-red-100 text-red-700',
  file_review: 'bg-orange-100 text-orange-700',
  proof_sent: 'bg-amber-100 text-amber-700',
  awaiting_approval: 'bg-yellow-100 text-yellow-700',
  production: 'bg-sky-100 text-sky-700',
  shipped: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-[#E8F1E6] text-[#2F5D3A]',
};

const NEXT_ACTION_FOR = {
  paid:               'Review Files',
  file_review:        'Send Proof',
  proof_sent:         'Wait Customer',
  awaiting_approval:  'Wait Customer',
  proof_approved:     'Send Supplier',
  in_production:      'Await Tracking',
  production:         'Await Tracking',
  shipped:            'Wait Delivery',
  completed:          '—',
};

const formatAge = (hours) => {
  if (!hours || hours <= 0) return 'just now';
  if (hours < 1) return '<1h ago';
  if (hours < 24) return `${Math.round(hours)}h ago`;
  const d = Math.floor(hours / 24);
  return d === 1 ? '1d ago' : `${d}d ago`;
};

const ageClass = (hours) => {
  if (hours > 168) return 'text-red-600 font-bold';
  if (hours > 48) return 'text-orange-600 font-semibold';
  return 'text-[#1F3D2C]/65';
};

export const MissionControlOrderRow = ({ row, expanded, onToggle, onAction, onOpenModal }) => {
  const [partner, setPartner] = useState(null);
  const [sending, setSending] = useState(false);
  const [auditEntries, setAuditEntries] = useState(null);

  useEffect(() => {
    if (!expanded || auditEntries !== null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/orders/${row.id}/audit-log`,
          { withCredentials: true },
        );
        if (!cancelled) setAuditEntries(res.data?.entries || res.data || []);
      } catch {
        if (!cancelled) setAuditEntries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [expanded, row.id, auditEntries]);

  const handleSendToSupplier = async () => {
    if (!partner) {
      toast.error('Pick a supplier first.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/orders/${row.id}/send-to-supplier`,
        { partner_id: partner.id, cc_admin: true },
        { withCredentials: true },
      );
      if (res.data?.ok) {
        toast.success(
          `Sent to ${partner.name} (${res.data.attachments_count} file${res.data.attachments_count === 1 ? '' : 's'} attached).`,
        );
        onAction?.();
      } else {
        toast.error(`SMTP returned non-OK — order marked failed. ${partner.name} not notified.`);
        onAction?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send to supplier failed.');
    } finally {
      setSending(false);
    }
  };

  const stageLabel = STAGE_LABEL[row.stage] || row.stage || '—';
  const stageBg = STAGE_BG[row.stage] || 'bg-gray-100 text-gray-700';
  const nextAction = NEXT_ACTION_FOR[(row.status || '').toLowerCase()] || NEXT_ACTION_FOR[row.stage] || 'Review';

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border ${
        expanded ? 'border-[#6FBF73] ring-2 ring-[#6FBF73]/20' : 'border-[#E5E7EB]'
      } overflow-hidden transition`}
      data-testid={`mc-row-${row.id}`}
    >
      <div className="px-4 py-3 grid grid-cols-12 gap-3 items-center">
        {/* ─── LEFT — thumbnail + order # + customer + age ─── */}
        <button
          type="button"
          onClick={onToggle}
          className="col-span-12 lg:col-span-4 flex items-center gap-3 text-left min-w-0 group"
          aria-expanded={expanded}
          data-testid={`mc-row-toggle-${row.id}`}
        >
          <ProofThumbnail orderId={row.id} size={56} rounded="lg" alt={`Proof for ${row.order_number}`} />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#14281D] text-sm tracking-tight truncate group-hover:text-[#2F5D3A]">
              {row.order_number}
            </p>
            <p className="text-xs text-[#1F3D2C]/70 truncate">{row.customer_email || '—'}</p>
            <p className={`text-[11px] mt-0.5 ${ageClass(row.age_hours)}`}>
              <Clock className="w-3 h-3 inline -mt-0.5 mr-0.5" />
              {formatAge(row.age_hours)}
              <span className="text-gray-400 mx-1.5">·</span>
              {row.first_item_product || 'Custom'} · qty {row.items_count}
            </p>
          </div>
        </button>

        {/* ─── MID — stage / supplier / proof / next action ───
            Clicking the status area opens the full Order Management
            modal (iter114s.99 — user msg 403). */}
        <div
          className="col-span-12 lg:col-span-5 flex flex-wrap items-center gap-1.5 text-xs cursor-pointer rounded-lg -mx-1 px-1 py-0.5 hover:bg-[#F4FBF6] transition-colors"
          onClick={onOpenModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenModal?.(); } }}
          title="Open Order Management"
          data-testid={`mc-row-open-modal-${row.id}`}
        >
          <span className={`px-2 py-1 rounded-full font-semibold ${stageBg}`} data-testid={`mc-stage-${row.id}`}>
            {stageLabel}
          </span>
          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
            Next: <span className="text-[#2F5D3A] font-semibold">{nextAction}</span>
          </span>
          {/* iter114s.76 Phase 2 — reseller-tier + rush badges. Tier
              maps to the same colour palette used in the Pricing
              Catalog so admins recognise platinum/gold/silver at a
              glance. */}
          {row.reseller_tier && (
            <span
              className={`px-2 py-1 rounded-full font-bold uppercase text-[10px] tracking-wider ${
                row.reseller_tier === 'platinum'
                  ? 'bg-[#14281D] text-white'
                  : row.reseller_tier === 'gold'
                    ? 'bg-amber-400 text-[#14281D]'
                    : 'bg-slate-300 text-slate-800'
              }`}
              data-testid={`mc-reseller-${row.id}`}
            >
              ★ {row.reseller_tier} reseller
            </span>
          )}
          {row.is_rush && (
            <span
              className="px-2 py-1 rounded-full bg-red-100 text-red-700 font-bold uppercase text-[10px] tracking-wider animate-pulse"
              data-testid={`mc-rush-${row.id}`}
            >
              ⚡ Rush
            </span>
          )}
          {row.reseller_verification_requested && !row.reseller_tier && (
            <span
              className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-300 font-semibold uppercase text-[10px] tracking-wider"
              data-testid={`mc-reseller-request-${row.id}`}
              title="Customer self-flagged as a reseller — verify before assigning tier."
            >
              ✦ Reseller verification requested
            </span>
          )}
          {row.supplier_name ? (
            <span className="px-2 py-1 rounded-full bg-[#E8F1E6] text-[#2F5D3A] font-medium flex items-center gap-1">
              <Truck className="w-3 h-3" /> {row.supplier_name}
            </span>
          ) : (
            <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 font-medium">
              No supplier yet
            </span>
          )}
          {row.proof_file_id && (
            <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Proof
              {row.proof_revision_count > 0 && (
                <span className="ml-0.5 text-emerald-600">·rev {row.proof_revision_count}</span>
              )}
            </span>
          )}
          {row.partner_workorder_status === 'sent' && (
            <span className="px-2 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">
              Sent to supplier
            </span>
          )}
          {row.partner_workorder_status === 'failed' && (
            <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 font-medium flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Dispatch failed
            </span>
          )}
          {row.tracking_number && (
            <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">
              Tracking: {row.tracking_number}
            </span>
          )}
        </div>

        {/* ─── RIGHT — action buttons ─── */}
        <div className="col-span-12 lg:col-span-3 flex items-center justify-end gap-1.5">
          <span className="text-xs font-semibold text-gray-500 mr-1 hidden xl:inline">
            ${(row.total_price ?? 0).toFixed(2)}
          </span>
          <Button
            type="button"
            onClick={onOpenModal}
            variant="outline"
            className="border-[#4A7C59] text-[#2F5D3A] hover:bg-[#E8F1E6] text-xs px-3 py-1.5 h-auto rounded-full"
            data-testid={`mc-row-manage-${row.id}`}
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Manage
          </Button>
          <Button
            type="button"
            onClick={onToggle}
            className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white text-xs px-3 py-1.5 h-auto rounded-full"
            data-testid={`mc-row-review-${row.id}`}
          >
            {expanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5 mr-1" /> Close
              </>
            ) : (
              <>
                <FileText className="w-3.5 h-3.5 mr-1" /> Review
              </>
            )}
          </Button>
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            className="text-[#2F5D3A] hover:text-[#14281D] p-1 rounded transition"
            data-testid={`mc-row-chevron-${row.id}`}
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* ─── EXPANDED INLINE PANEL ─── */}
      {expanded && (
        <div
          className="bg-[#F4FBF6] border-t border-[#6FBF73]/30 px-4 py-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          data-testid={`mc-row-expanded-${row.id}`}
        >
          {/* Proof preview */}
          <div className="bg-white rounded-lg p-3 border border-[#E5E7EB]">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D3A] mb-2">
              Artwork + Die line
            </p>
            <ProofThumbnail
              orderId={row.id}
              size={180}
              rounded="lg"
              alt={`Proof preview for ${row.order_number}`}
              className="mx-auto"
            />
            <div className="mt-2 text-xs text-[#1F3D2C]/75 space-y-0.5">
              <p>
                <span className="text-gray-500">Created:</span>{' '}
                {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
              </p>
              <p>
                <span className="text-gray-500">Priority score:</span>{' '}
                <strong className="text-[#2F5D3A]">{row.priority_score}</strong>
              </p>
              <p>
                <span className="text-gray-500">Items:</span> {row.items_count}
              </p>
            </div>
          </div>

          {/* Supplier picker + send */}
          <div className="bg-white rounded-lg p-3 border border-[#E5E7EB] md:col-span-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D3A] mb-2">
              Assign Supplier
            </p>
            <SupplierPicker
              category={row.first_item_category}
              product={row.first_item_product}
              currentPartnerId={row.supplier_id}
              onChange={setPartner}
            />
            <Button
              type="button"
              onClick={handleSendToSupplier}
              disabled={!partner || sending}
              className="mt-3 w-full bg-[#6FBF73] hover:bg-[#4A7C59] text-white text-sm font-semibold rounded-full"
              data-testid={`mc-row-send-supplier-${row.id}`}
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-1.5" /> Send to Supplier
                </>
              )}
            </Button>
            <p className="mt-2 text-[10px] text-[#1F3D2C]/60 leading-snug">
              Auto-attaches artwork + proof PDF. Status advances to In Production on send.
            </p>
          </div>

          {/* Audit / dispatch history */}
          <div className="bg-white rounded-lg p-3 border border-[#E5E7EB]">
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D3A] mb-2">
              Quick attributes
            </p>
            {/* iter114s.77 — inline editors for reseller-tier + rush
                flag so admins don't have to touch Mongo or wait for
                Phase 3 checkout collection to see the badges in
                action. Auto-saves on change via POST /quick-attrs. */}
            <div className="space-y-2 mb-3">
              <label className="text-[10px] font-semibold text-[#14281D] block">
                Reseller tier
                <select
                  value={row.reseller_tier || ''}
                  onChange={async (e) => {
                    const tier = e.target.value;
                    try {
                      await axios.post(
                        `${API_URL}/api/admin/orders/${row.id}/quick-attrs`,
                        { reseller_tier: tier }, { withCredentials: true },
                      );
                      toast.success(tier ? `Marked ${tier} reseller.` : 'Cleared reseller tier.');
                      onAction?.();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Update failed.');
                    }
                  }}
                  className="mt-0.5 block w-full text-sm border border-[#6FBF73]/30 rounded px-2 py-1 bg-white"
                  data-testid={`mc-reseller-select-${row.id}`}
                >
                  <option value="">— None (regular customer) —</option>
                  <option value="silver">Silver reseller (1.10×)</option>
                  <option value="gold">Gold reseller (1.25×)</option>
                  <option value="platinum">Platinum reseller (1.40×)</option>
                </select>
              </label>
              <label className="text-[10px] font-semibold text-[#14281D] flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!row.is_rush}
                  onChange={async (e) => {
                    try {
                      await axios.post(
                        `${API_URL}/api/admin/orders/${row.id}/quick-attrs`,
                        { is_rush: e.target.checked }, { withCredentials: true },
                      );
                      toast.success(e.target.checked ? 'Marked as rush.' : 'Cleared rush flag.');
                      onAction?.();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Update failed.');
                    }
                  }}
                  className="w-3.5 h-3.5 accent-[#6FBF73]"
                  data-testid={`mc-rush-checkbox-${row.id}`}
                />
                Mark as ⚡ Rush order
              </label>
            </div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D3A] mb-2">
              Dispatch History
            </p>
            {auditEntries === null ? (
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : auditEntries.length === 0 ? (
              <p className="text-xs text-[#1F3D2C]/60 italic">
                No dispatch history yet — first send will land here.
              </p>
            ) : (
              <ul className="text-xs space-y-1.5 max-h-44 overflow-y-auto pr-1">
                {auditEntries
                  .filter((e) => e.action !== 'edit' || e.field)
                  .slice(0, 12)
                  .map((entry, i) => (
                    <li key={entry.id || i} className="border-b border-gray-100 pb-1 last:border-0">
                      <p className="font-semibold text-[#14281D]">
                        {entry.action?.replace(/_/g, ' ') || 'event'}
                      </p>
                      <p className="text-gray-500">
                        {entry.actor || 'system'}
                        {entry.audit_ts && ` · ${new Date(entry.audit_ts).toLocaleString()}`}
                      </p>
                      {entry.partner_name && (
                        <p className="text-[#2F5D3A]">
                          → {entry.partner_name} ({entry.result})
                        </p>
                      )}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MissionControlOrderRow;
